import { useEffect, useState, useCallback } from "react";
import { RefreshCw, Scale, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const SOURCES = [["manual", "Manual targets"], ["mvo_max_sharpe", "MVO Max-Sharpe"], ["mvo_min_vol", "MVO Min-Vol"]];
const statusColor = (st) => (st === "over" ? "#F59E0B" : st === "under" ? "#3B82F6" : "#10B981");

// Phase F — Settings-driven Rebalancing / MVO section (compliance-first).
// Owns the Rebalancing tab: when mode = off everything (incl. legacy panel)
// is hidden behind an explanatory card.
export const RebalanceControls = ({ legacy }) => {
  const [cfg, setCfg] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [targetsDraft, setTargetsDraft] = useState(null);

  const loadStatus = useCallback(() => {
    setLoading(true);
    api.get("/rebalance/status").then(({ data }) => setStatus(data))
      .catch((e) => console.warn("rebalance status:", e?.message))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    api.get("/rebalance/settings").then(({ data }) => setCfg(data)).catch(() => {});
    loadStatus();
  }, [loadStatus]);

  const save = async (updates) => {
    try {
      const { data } = await api.put("/rebalance/settings", updates);
      setCfg(data);
      loadStatus();
    } catch (e) { toast.error("Could not save rebalancing settings"); }
  };
  const markReviewed = async () => {
    try { await api.post("/rebalance/mark-reviewed"); toast.success("Marked as reviewed"); loadStatus(); }
    catch (e) { toast.error("Failed"); }
  };

  if (!cfg) return <div className="py-6 flex justify-center"><RefreshCw className="w-4 h-4 animate-spin" style={{ color: "var(--hoip-text-tertiary)" }} /></div>;

  const trig = cfg.triggers || {};
  const rows = status?.rows || [];
  const editTargets = targetsDraft ?? {
    manual_targets: { ...(cfg.manual_targets || {}) },
    bounds: { ...(cfg.bounds || {}) },
    locked_tickers: [...(cfg.locked_tickers || [])],
    cash_target_pct: cfg.cash_target_pct || 0,
  };
  const tickers = rows.map(r => r.ticker);

  const inputCls = "w-14 h-5 px-1 text-[10px] font-mono-data border rounded-sm bg-transparent text-right";
  const inputStyle = { borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" };

  return (
    <div className="space-y-3" data-testid="rebalance-controls">
      {/* ── Mode / source / triggers bar ── */}
      <div className="border p-3" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div className="flex items-center gap-2">
            <Scale className="w-3.5 h-3.5" style={{ color: "#A78BFA" }} />
            <h3 className="text-xs font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>Rebalancing — Settings-Driven</h3>
            {loading && <RefreshCw className="w-3 h-3 animate-spin" style={{ color: "var(--hoip-text-tertiary)" }} />}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>Mode</label>
            <select value={cfg.mode} onChange={(e) => save({ mode: e.target.value })}
              className="h-6 px-1 text-[10px] border rounded-sm bg-transparent" style={inputStyle} data-testid="rebalance-mode-select">
              <option value="informational">Informational only</option>
              <option value="off">Off</option>
            </select>
            <label className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>Targets</label>
            <select value={cfg.target_source} onChange={(e) => save({ target_source: e.target.value })}
              className="h-6 px-1 text-[10px] border rounded-sm bg-transparent" style={inputStyle} data-testid="rebalance-source-select">
              {SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
        {cfg.mode === "off" ? (
          <p className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="rebalance-off-note">
            Rebalancing checks are <strong>Off</strong> — nothing is computed or shown on the dashboard. Switch to "Informational only" to see drift vs your targets.
          </p>
        ) : (
        <div className="flex items-center gap-4 flex-wrap" data-testid="rebalance-triggers-config">
          {/* drift trigger */}
          <label className="flex items-center gap-1.5 text-[10px]" style={{ color: "var(--hoip-text-secondary)" }}>
            <input type="checkbox" checked={!!trig.drift?.enabled}
              onChange={(e) => save({ triggers: { ...trig, drift: { ...trig.drift, enabled: e.target.checked } } })}
              data-testid="trigger-drift-enabled" />
            Drift ≥
            <input className={inputCls} style={inputStyle} type="number" defaultValue={trig.drift?.threshold_pct ?? 5}
              onBlur={(e) => save({ triggers: { ...trig, drift: { ...trig.drift, threshold_pct: parseFloat(e.target.value) || 5 } } })}
              data-testid="trigger-drift-threshold" />%
          </label>
          {/* calendar trigger */}
          <label className="flex items-center gap-1.5 text-[10px]" style={{ color: "var(--hoip-text-secondary)" }}>
            <input type="checkbox" checked={!!trig.calendar?.enabled}
              onChange={(e) => save({ triggers: { ...trig, calendar: { ...trig.calendar, enabled: e.target.checked } } })}
              data-testid="trigger-calendar-enabled" />
            Calendar
            <select value={trig.calendar?.frequency || "monthly"}
              onChange={(e) => save({ triggers: { ...trig, calendar: { ...trig.calendar, frequency: e.target.value } } })}
              className="h-5 px-1 text-[10px] border rounded-sm bg-transparent" style={inputStyle} data-testid="trigger-calendar-freq">
              <option value="monthly">monthly</option>
              <option value="quarterly">quarterly</option>
            </select>
          </label>
          {/* profit trigger */}
          <label className="flex items-center gap-1.5 text-[10px]" style={{ color: "var(--hoip-text-secondary)" }}>
            <input type="checkbox" checked={!!trig.profit?.enabled}
              onChange={(e) => save({ triggers: { ...trig, profit: { ...trig.profit, enabled: e.target.checked } } })}
              data-testid="trigger-profit-enabled" />
            Position up ≥
            <input className={inputCls} style={inputStyle} type="number" defaultValue={trig.profit?.gain_pct ?? 25}
              onBlur={(e) => save({ triggers: { ...trig, profit: { ...trig.profit, gain_pct: parseFloat(e.target.value) || 25 } } })}
              data-testid="trigger-profit-gain" />%
          </label>
          <button onClick={markReviewed} className="flex items-center gap-1 text-[9px] px-2 py-1 border rounded-sm hover:bg-[var(--hoip-hover)]"
            style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }} data-testid="mark-reviewed-btn">
            <CheckCircle2 className="w-3 h-3" /> Mark reviewed
            {status?.last_reviewed_at && <span style={{ color: "var(--hoip-text-tertiary)" }}>({status.last_reviewed_at.slice(0, 10)})</span>}
          </button>
        </div>
        )}
        {/* trigger status lights */}
        {cfg.mode !== "off" && status?.triggers && (
          <div className="flex items-center gap-3 flex-wrap mt-2" data-testid="rebalance-trigger-status">
            {status.triggers.map(t => (
              <span key={t.type} className="flex items-center gap-1 text-[9px]" title={t.detail}>
                <span className="w-2 h-2 rounded-full inline-block"
                  style={{ background: !t.enabled ? "#3F3F46" : t.fired ? "#F59E0B" : "#10B981" }} />
                <span style={{ color: "var(--hoip-text-tertiary)" }}>{t.type}{!t.enabled ? " (off)" : t.fired ? " — triggered" : " — not triggered"}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {cfg.mode !== "off" && (
        <>
          {/* ── Drift table ── */}
          <div className="border" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }} data-testid="rebalance-drift-table">
            <div className="px-3 py-2 border-b flex items-center justify-between flex-wrap gap-2" style={{ borderColor: "var(--hoip-border)" }}>
              <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: "var(--hoip-text-secondary)" }}>
                Drift vs {SOURCES.find(s2 => s2[0] === cfg.target_source)?.[1]}
              </span>
              {status?.mvo && (
                <span className="text-[8px] font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="rebalance-mvo-meta">
                  MVO run {status.mvo.computed_at?.slice(0, 10)} · ret {status.mvo.return_pct}% · vol {status.mvo.volatility_pct}% · Sharpe {status.mvo.sharpe}
                </span>
              )}
            </div>
            {status?.mvo_missing ? (
              <p className="px-3 py-3 text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="rebalance-mvo-missing">{status.message}</p>
            ) : rows.length === 0 ? (
              <p className="px-3 py-3 text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>{status?.message || "Loading drift…"}</p>
            ) : (
              <table className="w-full dense-table text-[10px]">
                <thead><tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
                  <th className="text-left font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Ticker</th>
                  <th className="text-right font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Current</th>
                  <th className="text-right font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Target</th>
                  <th className="text-right font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Drift</th>
                  <th className="text-right font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Drift $</th>
                  <th className="text-right font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Free / Locked sh</th>
                  <th className="text-left font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Status</th>
                </tr></thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.ticker} className="border-b" style={{ borderColor: "var(--hoip-divider)" }} data-testid={`drift-row-${r.ticker}`}>
                      <td className="font-mono-data font-bold">{r.ticker}</td>
                      <td className="text-right font-mono-data">{r.current_pct}%</td>
                      <td className="text-right font-mono-data">{r.target_pct}%</td>
                      <td className="text-right font-mono-data font-medium" style={{ color: statusColor(r.status) }}>{r.drift_pct > 0 ? "+" : ""}{r.drift_pct}%</td>
                      <td className="text-right font-mono-data" style={{ color: "var(--hoip-text-secondary)" }}>${Math.abs(r.drift_usd).toLocaleString()}</td>
                      <td className="text-right font-mono-data" title={r.locked_note || "No shares committed to option campaigns"}>
                        {r.free_shares}{r.locked_shares > 0 && <span style={{ color: "#F59E0B" }}> / {r.locked_shares} 🔒</span>}
                      </td>
                      <td className="text-[9px] font-bold uppercase" style={{ color: statusColor(r.status) }}>{r.status === "on_target" ? "on target" : r.status}</td>
                    </tr>
                  ))}
                  {status?.cash && (
                    <tr className="border-b" style={{ borderColor: "var(--hoip-divider)" }}>
                      <td className="font-mono-data font-bold" style={{ color: "var(--hoip-text-tertiary)" }}>CASH</td>
                      <td className="text-right font-mono-data">{status.cash.current_pct}%</td>
                      <td className="text-right font-mono-data">{status.cash.target_pct}%</td>
                      <td className="text-right font-mono-data">{status.cash.drift_pct > 0 ? "+" : ""}{status.cash.drift_pct}%</td>
                      <td colSpan={3} />
                    </tr>
                  )}
                </tbody>
              </table>
            )}
            {(status?.constraint_notes || []).length > 0 && (
              <div className="px-3 py-1.5 border-t space-y-0.5" style={{ borderColor: "var(--hoip-divider)" }} data-testid="rebalance-constraint-notes">
                {status.constraint_notes.map((n, i) => <p key={i} className="text-[8px]" style={{ color: "var(--hoip-text-tertiary)" }}>· {n}</p>)}
              </div>
            )}
            <p className="px-3 py-1.5 text-[8px] italic border-t" style={{ color: "var(--hoip-text-tertiary)", borderColor: "var(--hoip-divider)" }}>
              {status?.disclaimer || "Mechanical comparison against targets you configured. Informational only — not a recommendation."}
              {status?.csp_cash_committed > 0 && ` CSP collateral committed: $${status.csp_cash_committed.toLocaleString()} (cash, not shares).`}
            </p>
          </div>

          {/* ── Manual targets / bounds / locks editor ── */}
          <div className="border p-3" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }} data-testid="rebalance-targets-editor">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: "var(--hoip-text-secondary)" }}>Targets, bounds &amp; locks</span>
              <div className="flex items-center gap-2">
                <label className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>Cash target %</label>
                <input className={inputCls} style={inputStyle} type="number" value={editTargets.cash_target_pct}
                  onChange={(e) => setTargetsDraft({ ...editTargets, cash_target_pct: parseFloat(e.target.value) || 0 })}
                  data-testid="cash-target-input" />
                <button onClick={() => { save(editTargets); setTargetsDraft(null); }}
                  className="text-[9px] px-2.5 py-1 rounded-sm bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-targets-btn">Save targets</button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-1.5">
              {tickers.map(t => {
                const b = editTargets.bounds[t] || {};
                const locked = editTargets.locked_tickers.includes(t);
                return (
                  <div key={t} className="flex items-center gap-1.5 text-[9px]" data-testid={`target-editor-${t}`}>
                    <span className="font-mono-data font-bold w-10">{t}</span>
                    <input className={inputCls} style={inputStyle} type="number" placeholder="tgt%" value={editTargets.manual_targets[t] ?? ""}
                      onChange={(e) => setTargetsDraft({ ...editTargets, manual_targets: { ...editTargets.manual_targets, [t]: e.target.value === "" ? undefined : parseFloat(e.target.value) } })}
                      title="Manual target weight % (used when Targets = Manual)" />
                    <input className={inputCls} style={inputStyle} type="number" placeholder="min" value={b.min ?? ""}
                      onChange={(e) => setTargetsDraft({ ...editTargets, bounds: { ...editTargets.bounds, [t]: { ...b, min: e.target.value === "" ? undefined : parseFloat(e.target.value) } } })}
                      title="Min weight % bound (applies to MVO-constrained targets)" />
                    <input className={inputCls} style={inputStyle} type="number" placeholder="max" value={b.max ?? ""}
                      onChange={(e) => setTargetsDraft({ ...editTargets, bounds: { ...editTargets.bounds, [t]: { ...b, max: e.target.value === "" ? undefined : parseFloat(e.target.value) } } })}
                      title="Max weight % bound" />
                    <label className="flex items-center gap-0.5" title="Lock: target pinned to current weight" style={{ color: "var(--hoip-text-tertiary)" }}>
                      <input type="checkbox" checked={locked}
                        onChange={(e) => setTargetsDraft({ ...editTargets, locked_tickers: e.target.checked ? [...editTargets.locked_tickers, t] : editTargets.locked_tickers.filter(x => x !== t) })} />🔒
                    </label>
                  </div>
                );
              })}
            </div>
            <p className="text-[8px] italic mt-2" style={{ color: "var(--hoip-text-tertiary)" }}>
              Columns: manual target % · min bound % · max bound % · lock (pin to current weight). Bounds and locks also constrain the MVO reference portfolios. Max single position ({status?.settings ? "" : ""}from Settings → Risk Limits) always applies.
            </p>
          </div>

          {/* legacy sector / wheel-aware analysis */}
          {legacy}
        </>
      )}
    </div>
  );
};

// ═══ Row-6 dashboard mini — compact rebalancing status ═══
export const RebalanceMini = () => {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    api.get("/rebalance/status").then(({ data }) => setStatus(data))
      .catch((e) => console.warn("rebalance mini:", e?.message));
  }, []);
  const rows = (status?.rows || []).slice(0, 6);
  const srcLabel = SOURCES.find(s2 => s2[0] === status?.target_source)?.[1] || "—";
  return (
    <div className="border p-3 flex flex-col" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }} data-testid="rebalance-mini-panel">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <Scale className="w-3.5 h-3.5" style={{ color: "#A78BFA" }} />
          <span className="text-xs font-bold uppercase tracking-wide" style={{ color: "var(--hoip-text-secondary)" }}>Rebalancing · MVO</span>
          {status && status.mode !== "off" && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold"
              style={status.any_fired ? { color: "#F59E0B", background: "rgba(245,158,11,0.10)" } : { color: "#10B981", background: "rgba(16,185,129,0.10)" }}
              data-testid="rebalance-mini-badge">{status.any_fired ? "TRIGGERED" : "IN BAND"}</span>
          )}
        </div>
        <a href="/portfolio?tab=rebalancing" className="text-[10px] underline" style={{ color: "var(--hoip-info)" }} data-testid="rebalance-mini-open">Configure</a>
      </div>
      {!status ? (
        <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>Loading…</span>
      ) : status.mode === "off" ? (
        <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="rebalance-mini-off">Rebalancing checks are Off — enable Informational mode in Portfolio → Rebalancing.</span>
      ) : (
        <>
          <div className="flex items-center gap-3 flex-wrap mb-2">
            <span className="text-[11px]" style={{ color: "var(--hoip-text-tertiary)" }}>vs <span className="font-bold" style={{ color: "var(--hoip-text-secondary)" }}>{srcLabel}</span></span>
            {(status.triggers || []).map(t => (
              <span key={t.type} className="flex items-center gap-1 text-[10px]" title={t.detail}>
                <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: !t.enabled ? "#3F3F46" : t.fired ? "#F59E0B" : "#10B981" }} />
                <span style={{ color: "var(--hoip-text-tertiary)" }}>{t.type}</span>
              </span>
            ))}
          </div>
          {status.mvo_missing ? (
            <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>{status.message}</span>
          ) : rows.map(r => (
            <div key={r.ticker} className="flex items-center gap-2 py-[5px]" title={`current ${r.current_pct}% vs target ${r.target_pct}%${r.locked_note ? ` · ${r.locked_note}` : ""}`} data-testid={`rebalance-mini-row-${r.ticker}`}>
              <span className="font-mono-data text-[11px] font-bold w-12">{r.ticker}</span>
              <div className="relative flex-1 h-[8px] rounded-sm overflow-hidden" style={{ background: "var(--hoip-hover)" }}>
                <div className="absolute inset-y-0 left-0" style={{ width: `${Math.min(100, r.current_pct * 2)}%`, background: statusColor(r.status), opacity: 0.75 }} />
                <div className="absolute inset-y-0 w-px" style={{ left: `${Math.min(100, r.target_pct * 2)}%`, background: "var(--hoip-text-primary)" }} />
              </div>
              <span className="font-mono-data text-[10px] w-14 text-right" style={{ color: statusColor(r.status) }}>{r.drift_pct > 0 ? "+" : ""}{r.drift_pct}%</span>
              {r.locked_shares > 0 && <span className="text-[10px]" title={r.locked_note}>🔒</span>}
            </div>
          ))}
          <p className="text-[10px] italic mt-auto pt-2" style={{ color: "var(--hoip-text-tertiary)" }}>
            Drift vs your configured targets · bar = current, tick = target. Informational only — not advice.
          </p>
        </>
      )}
    </div>
  );
};
